import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import CreateTemplateForm from '../components/Labels/CreateTemplateForm';
import TemplateList from '../components/Labels/TemplateList';
import { labelTemplates } from '../utils/api';

const LabelsContent = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const data = await labelTemplates.getAll();
      setTemplates(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDelete = async (template) => {
    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      await labelTemplates.delete(template.id);

      await fetchTemplates();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setShowCreateForm(true);
  };

  const handleCreateSuccess = () => {
    setShowCreateForm(false);
    setSelectedTemplate(null);
    fetchTemplates();
  };

  return (
    <>
      <div className="flex-1 min-h-0">
        <div className="h-full border rounded-lg shadow-sm bg-white overflow-hidden">
          <div className="p-6">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h1 className="text-xl font-semibold text-gray-900">Label Templates</h1>
                <p className="mt-2 text-sm text-gray-700">
                  Create and manage templates for QR codes, barcodes, and text labels.
                </p>
              </div>
              <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(true)}
                  className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                  New Template
                </button>
              </div>
            </div>

            {error && (
              <div className="mt-4 bg-red-50 p-4 rounded-md">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="mt-6">
              {loading ? (
                <div className="text-center py-6 text-gray-500">Loading templates...</div>
              ) : (
                <TemplateList
                  templates={templates}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <CreateTemplateForm
        open={showCreateForm}
        onClose={() => {
          setShowCreateForm(false);
          setSelectedTemplate(null);
        }}
        onSuccess={handleCreateSuccess}
        template={selectedTemplate}
      />
    </>
  );
};

export default LabelsContent;
